import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, X, Calendar, Globe, ArrowLeft, ArrowRight, RotateCw } from "lucide-react";
import { PWHL_TAB_ICONS, INSIGHTS_TAB_ICONS } from "./Layout";

// Browser-style tabs that only appear inside the installed desktop app (not in a browser tab).
const LS = "rosterly_app_tabs";
const FORCE = "rosterly_force_app_tabs";
const H = 42;
const LAUNCH = typeof window !== "undefined" ? window.location.pathname : "";
const uid = () => Math.random().toString(36).slice(2, 9);
const cleanTitle = (t) => String(t || "Rosterly").replace(/\s*[|—–-]\s*Rosterly\s*$/i, "").replace(/^Rosterly\s*[|—–-]\s*/i, "").trim() || "Rosterly";

const isAppWindow = () => {
    if (typeof window === "undefined") return false;
    const q = new URLSearchParams(window.location.search).get("apptabs");
    if (q === "1") localStorage.setItem(FORCE, "1");
    if (q === "0") localStorage.removeItem(FORCE);
    const app = ["standalone", "window-controls-overlay", "minimal-ui"].some((m) => window.matchMedia(`(display-mode: ${m})`).matches);
    return (app || localStorage.getItem(FORCE) === "1") && window.innerWidth >= 1024;
};

const load = () => {
    try {
        const s = JSON.parse(localStorage.getItem(LS));
        if (s?.tabs?.length && s.tabs.some((t) => t.id === s.active)) return s;
    } catch { /* ignore */ }
    return null;
};

function useAppWindow() {
    const [on, setOn] = useState(isAppWindow);
    const [wco, setWco] = useState(() => !!navigator.windowControlsOverlay?.visible);
    useEffect(() => {
        const f = () => { setOn(isAppWindow()); setWco(!!navigator.windowControlsOverlay?.visible); };
        window.addEventListener("resize", f);
        navigator.windowControlsOverlay?.addEventListener?.("geometrychange", f);
        return () => { window.removeEventListener("resize", f); navigator.windowControlsOverlay?.removeEventListener?.("geometrychange", f); };
    }, []);
    return { on, wco };
}

const SEG_KEY = { rosters: "players", lineups: "roster", "active-lineup": "roster", "team-draft": "draft", "team-demographics": "demographics", "team-records": "records", "team-news": "news", "team-leaderboards": "leaderboards", "team-pages": "overview" };
const pageIcon = (path = "") => {
    const segs = path.split(/[?#]/)[0].split("/").filter((s) => s && !["nhl", "nat", "pwhl"].includes(s));
    const s = segs[0] || "";
    return INSIGHTS_TAB_ICONS[`/${s}`] || PWHL_TAB_ICONS[SEG_KEY[s] || s] || (s === "game" || s === "games" ? Calendar : Globe);
};

// Dragging a tab off the bar (e.g. onto another monitor) pops it out as its own app window.
const popOut = (t, e, onClose) => {
    const bar = document.querySelector('[data-testid="app-tabs-bar"]')?.getBoundingClientRect();
    const inBar = bar && e.clientY >= bar.top - 20 && e.clientY <= bar.bottom + 40 && e.clientX >= bar.left && e.clientX <= bar.right && !(e.clientX === 0 && e.clientY === 0);
    if (inBar) return;
    const w = Math.max(900, window.outerWidth * 0.8), hgt = Math.max(700, window.outerHeight * 0.8);
    const win = window.open(t.path, "_blank", `popup,width=${Math.round(w)},height=${Math.round(hgt)},left=${Math.round(e.screenX - 120)},top=${Math.round(e.screenY - 20)}`);
    if (win) onClose(t.id);
};

function Tab({ t, active, onPick, onClose, onDrag, onMenu, icon, h }) {
    const PI = pageIcon(t.path);
    return (
        <div draggable onDragStart={(e) => e.dataTransfer.setData("text/tab", t.id)} onDragOver={(e) => e.preventDefault()}
            onDragEnd={(e) => { if (e.dataTransfer.dropEffect === "none") popOut(t, e, onClose); }}
            onDrop={(e) => onDrag(e.dataTransfer.getData("text/tab"), t.id)}
            onMouseDown={(e) => { if (e.button === 1 && !t.pinned) { e.preventDefault(); onClose(t.id); } }}
            onContextMenu={(e) => { e.preventDefault(); onMenu(t.id, e.clientX, e.clientY); }}
            onClick={() => onPick(t.id)} title={`${t.title}${t.pinned ? " (pinned)" : ""} — right-click for options`}
            style={{ height: h }}
            className={`group relative flex items-center ${t.pinned ? "justify-center w-[46px] shrink-0" : "gap-2 min-w-[120px] max-w-[240px] flex-1 px-3"} rounded-t-lg cursor-default select-none transition-colors ${active ? "bg-[#0A0B10] text-white" : "text-white/55 hover:bg-white/[0.05] hover:text-white/85"}`}
            data-testid={`app-tab-${t.id}`} data-active={active ? "1" : "0"} data-pinned={t.pinned ? "1" : "0"}>
            {t.pinned ? <PI className="h-4 w-4 shrink-0" strokeWidth={2} /> : (
                <>
                    <img src={icon} alt="" className="h-4 w-4 shrink-0 rounded-sm" />
                    <span className="h-3.5 w-px bg-white/15 shrink-0" aria-hidden="true" />
                    <PI className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span className="flex-1 min-w-0 truncate text-[12px] font-semibold">{t.title || "Rosterly"}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onClose(t.id); }} aria-label="Close tab"
                        className={`h-4 w-4 shrink-0 inline-flex items-center justify-center rounded hover:bg-white/15 ${active ? "opacity-80" : "opacity-0 group-hover:opacity-70"}`}
                        data-testid={`app-tab-close-${t.id}`}><X className="h-3 w-3" /></button>
                </>
            )}
        </div>
    );
}

function TabMenu({ menu, tab, onPin, onClose, onCloseOthers, onDone }) {
    useEffect(() => {
        const off = () => onDone();
        window.addEventListener("mousedown", off);
        window.addEventListener("blur", off);
        return () => { window.removeEventListener("mousedown", off); window.removeEventListener("blur", off); };
    }, [onDone]);
    if (!tab) return null;
    const item = "w-full text-left px-3 py-1.5 text-[11px] font-semibold text-white/80 hover:bg-white/10 hover:text-white";
    const act = (fn) => (e) => { e.stopPropagation(); fn(tab.id); onDone(); };
    return (
        <div onMouseDown={(e) => e.stopPropagation()} className="fixed z-[200] min-w-[160px] py-1 rounded-lg border border-white/10 bg-[#11131B] shadow-xl shadow-black/50"
            style={{ left: menu.x, top: menu.y, WebkitAppRegion: "no-drag", appRegion: "no-drag" }} data-testid="app-tab-menu">
            <button type="button" className={item} onClick={act(onPin)} data-testid="app-tab-menu-pin">{tab.pinned ? "Unpin tab" : "Pin tab"}</button>
            {!tab.pinned && <button type="button" className={item} onClick={act(onClose)} data-testid="app-tab-menu-close">Close tab</button>}
            <button type="button" className={item} onClick={act(onCloseOthers)} data-testid="app-tab-menu-close-others">Close other tabs</button>
        </div>
    );
}

export function AppTabs() {
    const { on, wco } = useAppWindow();
    const loc = useLocation();
    const nav = useNavigate();
    const here = loc.pathname + loc.search + loc.hash;
    const [st, setSt] = useState(() => load() || (() => { const id = uid(); return { tabs: [{ id, path: here, title: cleanTitle(document.title) }], active: id }; })());
    const stRef = useRef(st);
    stRef.current = st;
    const pending = useRef(null);
    const icon = document.querySelector('link[rel="icon"][sizes="32x32"]')?.href || "/favicon-32x32.png";

    // In the title bar (window-controls overlay) the strip is always there; otherwise only with 2+ tabs.
    const show = on && (wco || st.tabs.length > 1);
    const barH = wco ? Math.max(H, Math.round(navigator.windowControlsOverlay.getTitlebarAreaRect?.().height || H)) : H;
    useEffect(() => { localStorage.setItem(LS, JSON.stringify(st)); }, [st]);
    useEffect(() => {
        document.documentElement.style.setProperty("--app-tabs-h", show ? `${barH}px` : "0px");
        window.dispatchEvent(new Event("resize"));
    }, [show, barH]);

    // Restore the saved tab's page on launch (after the "/" landing redirect has run).
    const booted = useRef(false);
    const saved = useRef(st.tabs.find((t) => t.id === st.active)?.path);
    useEffect(() => {
        if (booted.current || !on) return;
        booted.current = true;
        const a = saved.current;
        if (a && LAUNCH === "/") setTimeout(() => nav(a, { replace: true }), 0);
    }, [on]); // eslint-disable-line react-hooks/exhaustive-deps

    // Active tab follows in-page navigation; title follows document.title.
    useEffect(() => {
        if (!on) return;
        setSt((s) => ({ ...s, tabs: s.tabs.map((t) => (t.id === s.active ? { ...t, path: here } : t)) }));
        if (pending.current != null) {
            const y = pending.current; pending.current = null;
            setTimeout(() => window.scrollTo(0, y), 60);
        }
    }, [here, on]);
    useEffect(() => {
        if (!on) return;
        const el = document.querySelector("title");
        const f = () => setSt((s) => ({ ...s, tabs: s.tabs.map((t) => (t.id === s.active ? { ...t, title: cleanTitle(document.title) } : t)) }));
        f();
        const mo = new MutationObserver(f);
        if (el) mo.observe(el, { childList: true, characterData: true, subtree: true });
        return () => mo.disconnect();
    }, [on, st.active]);

    const pick = useCallback((id) => {
        const s = stRef.current;
        if (id === s.active) return;
        const to = s.tabs.find((t) => t.id === id);
        if (!to) return;
        const tabs = s.tabs.map((t) => (t.id === s.active ? { ...t, scroll: window.scrollY } : t));
        setSt({ tabs, active: id });
        pending.current = to.scroll || 0;
        nav(to.path);
    }, [nav]);

    const open = useCallback((path = "/", focus = true) => {
        const id = uid();
        const s = stRef.current;
        const tabs = s.tabs.map((t) => (t.id === s.active ? { ...t, scroll: window.scrollY } : t));
        const i = tabs.findIndex((t) => t.id === s.active);
        tabs.splice(i + 1, 0, { id, path, title: focus ? "Rosterly" : (path.split(/[?#]/)[0].split("/").filter(Boolean).map((x) => (/^(ncaa|nhl|pwhl)$/i.test(x) ? x.toUpperCase() : x === "nat" ? "WWHC" : x.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))).join(" · ") || "Rosterly") });
        setSt({ tabs, active: focus ? id : s.active });
        if (focus) { pending.current = 0; nav(path); }
    }, [nav]);

    const close = useCallback((id) => {
        const s = stRef.current;
        if (s.tabs.length === 1) return;
        const i = s.tabs.findIndex((t) => t.id === id);
        const tabs = s.tabs.filter((t) => t.id !== id);
        if (id !== s.active) return setSt({ ...s, tabs });
        const next = tabs[Math.min(i, tabs.length - 1)];
        setSt({ tabs, active: next.id });
        pending.current = next.scroll || 0;
        nav(next.path);
    }, [nav]);

    const move = useCallback((from, to) => {
        if (!from || from === to) return;
        setSt((s) => {
            const tabs = [...s.tabs];
            const a = tabs.findIndex((t) => t.id === from), b = tabs.findIndex((t) => t.id === to);
            if (a < 0 || b < 0) return s;
            tabs.splice(b, 0, tabs.splice(a, 1)[0]);
            return { ...s, tabs };
        });
    }, []);

    // Ctrl/Cmd/middle-click on an in-app link opens it in a new background tab.
    useEffect(() => {
        if (!on) return;
        const handler = (e) => {
            if (!(e.ctrlKey || e.metaKey || e.button === 1)) return;
            const a = e.target.closest?.("a[href]");
            if (!a || a.target === "_blank") return;
            const u = new URL(a.href, window.location.href);
            if (u.origin !== window.location.origin || u.pathname.startsWith("/api")) return;
            e.preventDefault(); e.stopPropagation();
            open(u.pathname + u.search + u.hash, false);
        };
        document.addEventListener("click", handler, true);
        document.addEventListener("auxclick", handler, true);
        // Schedule game cards (middle-click) open their game in a new IN-APP tab.
        const onOpen = (e) => { if (e.detail?.path) open(e.detail.path, false); };
        window.addEventListener("rosterly:open-tab", onOpen);
        window.__rosterlyTabs = true;
        return () => { document.removeEventListener("click", handler, true); document.removeEventListener("auxclick", handler, true); window.removeEventListener("rosterly:open-tab", onOpen); window.__rosterlyTabs = false; };
    }, [on, open]);

    // Alt+T new · Alt+W close · Ctrl+Tab / Alt+1-9 switch.
    useEffect(() => {
        if (!on) return;
        const kd = (e) => {
            const s = stRef.current;
            const i = s.tabs.findIndex((t) => t.id === s.active);
            const k = e.key.toLowerCase();
            if (e.altKey && k === "t") { e.preventDefault(); open("/"); }
            else if (e.altKey && k === "w") { e.preventDefault(); if (!s.tabs.find((t) => t.id === s.active)?.pinned) close(s.active); }
            else if (e.ctrlKey && e.key === "Tab") { e.preventDefault(); const n = s.tabs.length; pick(s.tabs[(i + (e.shiftKey ? n - 1 : 1)) % n].id); }
            else if (e.altKey && /^[1-9]$/.test(e.key)) { e.preventDefault(); const t = e.key === "9" ? s.tabs[s.tabs.length - 1] : s.tabs[Number(e.key) - 1]; if (t) pick(t.id); }
        };
        window.addEventListener("keydown", kd);
        return () => window.removeEventListener("keydown", kd);
    }, [on, open, close, pick]);

    const [menu, setMenu] = useState(null);
    const pin = useCallback((id) => setSt((s) => {
        const tabs = s.tabs.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t));
        return { ...s, tabs: [...tabs.filter((t) => t.pinned), ...tabs.filter((t) => !t.pinned)] };
    }), []);
    const closeOthers = useCallback((id) => {
        const s = stRef.current;
        const tabs = s.tabs.filter((t) => t.pinned || t.id === id);
        const keep = tabs.some((t) => t.id === s.active) ? s.active : id;
        setSt({ tabs, active: keep });
        if (keep !== s.active) { const t = tabs.find((x) => x.id === keep); pending.current = t.scroll || 0; nav(t.path); }
    }, [nav]);

    if (!show) return null;
    const noDrag = { WebkitAppRegion: "no-drag", appRegion: "no-drag" };
    const tabH = Math.max(28, (wco ? barH : H) - 7);
    return (
        <div className="sticky top-0 z-[70] flex items-end gap-1 bg-[#05060A] border-b border-[#1E2233]"
            style={wco
                ? { height: barH, paddingLeft: "calc(env(titlebar-area-x, 0px) + 8px)", paddingRight: "calc(100vw - env(titlebar-area-x, 0px) - env(titlebar-area-width, 100vw) + 8px)", WebkitAppRegion: "drag", appRegion: "drag" }
                : { height: H, paddingLeft: 8, paddingRight: 8 }}
            data-testid="app-tabs-bar" data-keep-colors data-wco={wco ? "1" : "0"}>
            {/* Back · Refresh · Forward — browser-style controls for the installed app. */}
            <div className="flex items-center gap-0.5 shrink-0 mb-[3px] mr-1" style={noDrag} data-testid="app-tabs-nav">
                {[["back", ArrowLeft, "Back (Alt+←)", () => window.history.back()], ["refresh", RotateCw, "Refresh (F5)", () => window.location.reload()], ["forward", ArrowRight, "Forward (Alt+→)", () => window.history.forward()]].map(([k, Ic, t, fn]) => (
                    <button key={k} type="button" onClick={fn} title={t} aria-label={t} data-testid={`app-tab-${k}`}
                        className="h-[30px] w-[30px] inline-flex items-center justify-center rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors"><Ic className="h-4 w-4" /></button>
                ))}
            </div>
            <div className="flex items-end gap-1 flex-1 min-w-0 overflow-hidden">
                {st.tabs.map((t) => (
                    <div key={t.id} style={noDrag} className={t.pinned ? "flex shrink-0" : "flex-1 min-w-[120px] max-w-[240px] flex"}>
                        <Tab t={t} active={t.id === st.active} onPick={pick} onClose={close} onDrag={move} onMenu={(id, x, y) => setMenu({ id, x, y })} icon={icon} h={tabH} />
                    </div>
                ))}
                <button type="button" onClick={() => open("/")} title="New tab (Alt+T)" aria-label="New tab" style={noDrag}
                    className="h-[30px] w-[30px] mb-[3px] shrink-0 inline-flex items-center justify-center rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                    data-testid="app-tab-new"><Plus className="h-4 w-4" /></button>
            </div>
            {menu && <TabMenu menu={menu} tab={st.tabs.find((t) => t.id === menu.id)} onPin={pin} onClose={close} onCloseOthers={closeOthers} onDone={() => setMenu(null)} />}
        </div>
    );
}
